import { useLocation } from "react-router-dom";

import { FALLBACK_LANGUAGE, Locale, LOCALES } from "@/constants";
import { useUser } from "@/hooks/useUser";
import { User } from "@/types/User";

import useSearchParams from "./useSearchParams";

const asLocale = (v: unknown): Locale | undefined =>
  LOCALES.includes(v as Locale) ? (v as Locale) : undefined;

const useActiveLanguage = (): Locale => {
  // Try to get the user
  const { data } = useUser();
  const userLang = (data?.data as User)?.language;

  // Try to get the language from the url
  const { pathname } = useLocation();
  const urlLang = pathname.startsWith("/en")
    ? "en"
    : pathname.startsWith("/sv")
      ? "sv"
      : undefined;

  // If there's no part of the url with language, revert to using search params
  // NOTE: `language` is also the search filter's content-language param
  // (see useFilterParams), so it can be any Language code or an array of them.
  const [{ language: paramLang, locale: paramLang2 }] = useSearchParams<{
    language?: unknown;
    locale?: unknown;
  }>();

  const browserLang = navigator.language?.slice(0, 2);
  const resolvedBrowserLang =
    LOCALES.includes(browserLang as Locale) && (browserLang as Locale);

  // return in order of priority
  return (
    urlLang ||
    userLang ||
    asLocale(paramLang) ||
    asLocale(paramLang2) ||
    resolvedBrowserLang ||
    FALLBACK_LANGUAGE
  );
};

export default useActiveLanguage;
